import { useEffect, useState, useMemo } from "react";

import { filter } from "lodash";
import queryString from "query-string";

import { SWReactIcons } from "@similarweb/icons";
import {
    DomainsChipDownContainer,
    ListItemWebsite,
    SearchInput,
    Checkbox,
    PlainTooltip,
} from "@similarweb/ui-components";

import { ColumnsPickerLitePro } from "app/components/ColumnsPickerLitePro/ColumnsPickerLitePro";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { radioItems } from "app/components/React/PopularPagesFilters/popularPagesFilters.config";
import RadioSelect from "app/components/React/RadioSelect/radioSelect";
import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { i18nFilter } from "app/filters/i18nFilter";
import { DownloadExcelContainer, SearchContainer } from "app/pages/workspace/StyledComponent";
import { useDomainsFavicons } from "app/services/sitesService/hooks/useDomainsFavicons";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { TopStyled, Right, StyledCheckboxContainer, StyledInfoIcon, ChipdownItem } from "./styledComponents";

export interface IPagesFilters {
    orderBy: string;
    webSource: string;
    country: string;
    includeSubDomains: boolean;
    from: string;
    to: string;
    pageSize: number;
    page: number;
    timeGranularity: string;
    isWindow: boolean;
    keys: string;
    filter?: string;
    isAll?: boolean | null;
    isNew?: boolean | null;
    isTrending?: boolean | null;
    isEvergreen?: boolean | null;
    isUtm?: boolean | null;
}

interface IPagesTableTopProps {
    downloadExcelPermitted: boolean;
    excelLinkResource: string;
    tableColumns: any;
    tableOptions: any;
    onClickToggleColumns: () => void;
    initialFilters: IPagesFilters;
    onFilterChange: (filters: IPagesFilters) => void;
    searchValue?: string;
    domains: string[];
    showDomainSelector?: boolean;
    setSelectedDomainObj: (domain: { name: string; icon: string } | undefined) => void;
    selectedDomainObj?: { name: string; icon: string };
    totalCount: number;
    sort: string;
    asc: boolean;
}

const PagesTableTop = (props: IPagesTableTopProps) => {
    const services = useMemo(
        () => ({
            i18n: i18nFilter(),
        }),
        [],
    );
    const SEARCH_PREFIX = "Page;contains;";
    const resetFilters = {
        isAll: undefined,
        isNew: undefined,
        isTrending: undefined,
        isEvergreen: undefined,
    };

    type IRadioButtonValue = "isAll" | "isNew" | "isTrending" | "isEvergreen";
    type IDomainList = { name: string; icon: string }[];
    const {
        downloadExcelPermitted,
        excelLinkResource,
        tableColumns,
        tableOptions,
        onClickToggleColumns,
        initialFilters,
        onFilterChange,
        searchValue,
        domains = [],
        showDomainSelector = false,
        setSelectedDomainObj,
        selectedDomainObj,
        totalCount,
        sort,
        asc,
    } = props;
    const radioButtonItems = useMemo(() => {
        return radioItems.reduce(
            (
                validRadioItems,
                radioItem: {
                    value: IRadioButtonValue;
                    label: string;
                    tooltip: string;
                    tracking: string;
                },
            ) => {
                if (!initialFilters.isWindow || (initialFilters.isWindow && radioItem.value === "isAll")) {
                    validRadioItems.push({
                        id: radioItem.value,
                        text: services.i18n(radioItem.label),
                        tooltip: services.i18n(radioItem.tooltip),
                        trackingName: radioItem.tracking,
                    });
                }
                return validRadioItems;
            },
            [],
        );
    }, [initialFilters.isWindow]);

    const [filters, setFilters] = useState<IPagesFilters>(initialFilters);
    const [selectedRadioButton, setSelectedRadioButton] = useState<IRadioButtonValue>("isAll");
    const [isCheckedCheckbox, setIsCheckedCheckbox] = useState(false);
    const [selectedDomain, setSelectedDomain] = useState("");

    const { data: faviconsList } = useDomainsFavicons(domains, {
        enabled: domains.length > 0,
    });

    const domainList = useMemo<IDomainList>(() => {
        if (!faviconsList) {
            return [];
        }
        return faviconsList.map((domain) => ({
            name: domain.Domain,
            icon: domain.Favicon,
        }));
    }, [faviconsList]);

    const excelLink = useMemo(() => {
        const queryStringParams = queryString.stringify({ ...filters, sort, asc });
        return `${excelLinkResource}?${queryStringParams}`;
    }, [filters, excelLinkResource, sort, asc]);

    const domainItems = useMemo(() => {
        return domainList.map(({ name, icon }, index) => {
            return (
                <ListItemWebsite
                    key={index}
                    text={name}
                    img={icon}
                />
            );
        });
    }, [domainList]);

    const excelDownloadUrl = downloadExcelPermitted ? excelLink : "";

    const trackExcelDownload = () => {
        allTrackers.trackEvent("Download", "submit-ok", "Table/Excel");
    };

    useEffect(() => {
        const resolveSelectedDomain = selectedDomain
            ? domainList.find((domain) => domain.name === selectedDomain)
            : domainList[0];
        setSelectedDomainObj(resolveSelectedDomain);
    }, [selectedDomain, domainList]);

    useEffect(() => {
        setSelectedDomain(initialFilters.keys);
    }, []);
    useEffect(() => {
        onFilterChange(filters);
    }, [filters]);

    const onSearchInputChange = (text) => {
        allTrackers.trackEvent("Search Bar", "Change", `Search Bar/${text}`);
        setFilters((prevFilters) => ({
            ...prevFilters,
            filter: text ? `${SEARCH_PREFIX}"${decodeURIComponent(text)}"` : undefined,
        }));
    };
    const onRadioButtonClicked = (item) => {
        const selectedValueArray = filter(radioButtonItems, (radioItem) => {
            return radioItem.id === item;
        });
        const selectedValueObject = selectedValueArray[0];
        setSelectedRadioButton(selectedValueObject.id);
        const trackingId = selectedValueObject.trackingName;
        allTrackers.trackEvent("Filter", "click", trackingId);
        setFilters((prevFilters) => ({
            ...prevFilters,
            ...resetFilters,
            [selectedValueObject.id]: true,
        }));
    };
    const onCheckboxCheck = () => {
        allTrackers.trackEvent("Check box", isCheckedCheckbox ? "remove" : "click", "campaign(utm)");
        setFilters((prevFilters) => ({
            ...prevFilters,
            isUtm: !isCheckedCheckbox ? !isCheckedCheckbox : undefined,
        }));
        setIsCheckedCheckbox((isChecked) => !isChecked);
    };
    const onSelectDomain = (domain) => {
        const domainName = domain?.text;
        allTrackers.trackEvent("Drop down", "click", domainName);
        setSelectedDomain(domainName);
        setFilters((prevFilters) => ({
            ...prevFilters,
            keys: domainName,
        }));
    };

    const domainSelector = selectedDomainObj && (
        <ChipdownItem>
            <DomainsChipDownContainer
                width={200}
                onClick={onSelectDomain}
                selectedDomainText={selectedDomainObj.name}
                selectedDomainIcon={selectedDomainObj.icon}
                onCloseItem={onSelectDomain}
                buttonText={services.i18n("common.category.all")}
                noResultsMessage={services.i18n("dropdown.no.results")}
            >
                {domainItems}
            </DomainsChipDownContainer>
        </ChipdownItem>
    );

    return (
        <>
            <TopStyled>
                {showDomainSelector ? domainSelector : null}
                <RadioSelect
                    items={radioButtonItems}
                    selected={selectedRadioButton}
                    onSelect={onRadioButtonClicked}
                />
                <StyledCheckboxContainer>
                    <Checkbox
                        label={services.i18n("checkbox.utm.label")}
                        onClick={onCheckboxCheck}
                        selected={isCheckedCheckbox}
                    />
                    <PlainTooltip tooltipContent={services.i18n("checkbox.utm.tooltip")}>
                        <StyledInfoIcon>
                            <SWReactIcons
                                iconName="info"
                                size="xs"
                            />
                        </StyledInfoIcon>
                    </PlainTooltip>
                </StyledCheckboxContainer>
            </TopStyled>
            <SearchContainer>
                <SearchInput
                    disableClear={true}
                    defaultValue={searchValue}
                    debounce={400}
                    onChange={onSearchInputChange}
                    placeholder={services.i18n("forms.search.placeholder")}
                />
                <Right>
                    <FlexRow>
                        {downloadExcelPermitted ? (
                            <DownloadExcelContainer>
                                <DownloadButtonMenu
                                    totalCount={totalCount}
                                    Excel={true}
                                    downloadUrl={excelDownloadUrl}
                                    exportFunction={trackExcelDownload}
                                    excelLocked={!downloadExcelPermitted}
                                />
                            </DownloadExcelContainer>
                        ) : null}
                        <div>
                            <ColumnsPickerLitePro
                                onClickToggleColumns={onClickToggleColumns}
                                tableOptions={tableOptions}
                                tableColumns={tableColumns}
                                withTooltip
                            />
                        </div>
                        <div>
                            <AddToDashboard
                                webSource={initialFilters.webSource as IWidgetModelTypesWebSource}
                                type="Table"
                                metric="PopularPagesV2"
                                modelType="fromWebsite"
                                filters={filters}
                                overrideAddToDashboardParams={{
                                    key: [
                                        {
                                            image: selectedDomainObj?.icon,
                                            name: selectedDomainObj?.name,
                                        },
                                    ],
                                }}
                            />
                        </div>
                    </FlexRow>
                </Right>
            </SearchContainer>
        </>
    );
};

export default PagesTableTop;
